import { api, type ReturnDoc, type ReturnTypeT } from "../api";

// Backend page_size cheklovi (le=200) — sahifalab hammasini yig'amiz.
const PAGE = 200;

/**
 * Berilgan turdagi barcha qaytarishlarni (ixtiyoriy: bitta hujjat bo'yicha)
 * sahifalab to'liq yuklaydi. "Qaytariladigan qoldiq" hisobi uchun.
 */
export async function fetchAllReturns(
  return_type: ReturnTypeT,
  original_document_id?: number,
): Promise<ReturnDoc[]> {
  const out: ReturnDoc[] = [];
  let page = 1;
  for (;;) {
    const pg = await api.returns({
      return_type,
      original_document_id,
      page,
      page_size: PAGE,
    });
    out.push(...pg.items);
    if (out.length >= pg.total || pg.items.length === 0) break;
    page += 1;
  }
  return out;
}

/** original_document_id → (product_id → Σ qaytarilgan miqdor). */
export type ReturnedIndex = Map<number, Map<number, number>>;

export function buildReturnedIndex(docs: ReturnDoc[]): ReturnedIndex {
  const idx: ReturnedIndex = new Map();
  for (const d of docs) {
    let perDoc = idx.get(d.original_document_id);
    if (!perDoc) {
      perDoc = new Map();
      idx.set(d.original_document_id, perDoc);
    }
    for (const it of d.items) {
      perDoc.set(
        it.product_id,
        (perDoc.get(it.product_id) ?? 0) + Number(it.quantity),
      );
    }
  }
  return idx;
}

/** Hujjat qatorining qaytarish uchun kerakli qismi (Sale/Purchase lines mos). */
export type ReturnableLine = {
  product_id: number;
  product_nomi: string;
  miqdor: string;
  birlik_narx: string;
};

export type AggLine = {
  product_id: number;
  nomi: string;
  qty: number;
  avgPrice: number;
};

/**
 * Hujjat qatorlarini tovar bo'yicha yig'adi (bitta tovar bir necha qatorda
 * kelishi mumkin) — miqdor jami, narx vaznlangan o'rtacha (backend bilan mos).
 */
export function aggregateLines(lines: ReturnableLine[]): AggLine[] {
  const m = new Map<number, AggLine & { sum: number }>();
  for (const l of lines) {
    const qty = Number(l.miqdor) || 0;
    const cur = m.get(l.product_id);
    if (cur) {
      cur.qty += qty;
      cur.sum += qty * Number(l.birlik_narx);
    } else {
      m.set(l.product_id, {
        product_id: l.product_id,
        nomi: l.product_nomi,
        qty,
        avgPrice: 0,
        sum: qty * Number(l.birlik_narx),
      });
    }
  }
  return [...m.values()].map(({ sum, ...a }) => ({
    ...a,
    avgPrice: a.qty > 0 ? sum / a.qty : 0,
  }));
}

const EPS = 1e-9;

/** Hujjatda hali qaytariladigan tovar qolganmi (kasr miqdorlar uchun EPS). */
export function docHasReturnable(
  docId: number,
  lines: ReturnableLine[],
  idx: ReturnedIndex,
): boolean {
  const perDoc = idx.get(docId);
  return aggregateLines(lines).some(
    (a) => a.qty - (perDoc?.get(a.product_id) ?? 0) > EPS,
  );
}
